// File: src/pages/MyAnnualOKRs.js

import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaPlus, FaTrash } from 'react-icons/fa'
import './MyOKRs.css'
import { apiGet, apiPut, apiDelete } from '../utils/api'
import { AuthContext } from '../context/AuthContext'

export default function MyAnnualOKRs() {
  const { token } = useContext(AuthContext)
  const navigate  = useNavigate()

  const [okrs, setOkrs]               = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [expandedMap, setExpandedMap] = useState({})

  // always false on this page
  const isQuarterly = false

  // load annual OKRs
  useEffect(() => {
    async function fetchAnnualOKRs() {
      setLoading(true)
      try {
        const res = await apiGet('/api/okrs/annual', token)
        if (!res.ok) throw new Error('Failed to fetch ANNUAL OKRs')
        const data = await res.json()
        setOkrs(data)
        // initialize expand/collapse map
        const init = {}
        data.forEach(o => { init[o._id] = false })
        setExpandedMap(init)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchAnnualOKRs()
  }, [token])

  // navigation
  const handleCreateOKR = () => {
    navigate('/createokr', { state: { type: 'annual' } })
  }
  const handleEditOKR = id => navigate(`/editokr/${id}`)
  const switchToQuarter = () => navigate('/company-okrs/my-okrs')

  // toggle a key result's checkbox
  const handleToggleKR = async (okrId, idx) => {
    const target = okrs.find(o => o._id === okrId);
    if (!target || !target.keyResults || !target.keyResults[idx]) {
      console.error('❌ [MyAnnualOKRs] Invalid OKR or key result index:', { okrId, idx, target: !!target });
      return;
    }
    
    // Toggle the completed status
    const currentCompleted = target.keyResults[idx].completed || false;
    const newCompleted = !currentCompleted;
    
    console.log('🔄 [MyAnnualOKRs] Toggling KR:', { 
      okrId, 
      idx, 
      current: currentCompleted, 
      new: newCompleted,
      krText: target.keyResults[idx].text?.substring(0, 30)
    });
    
    // Create new keyResults array with updated completed status
    const krs = target.keyResults.map((kr, i) => {
      if (i === idx) {
        return {
          text: kr.text || '',
          team: kr.team || undefined,
          krOwner: kr.krOwner || undefined,
          completed: newCompleted, // Explicit boolean
          deadline: kr.deadline || undefined
        };
      }
      return {
        text: kr.text || '',
        team: kr.team || undefined,
        krOwner: kr.krOwner || undefined,
        completed: kr.completed === true || kr.completed === 'true' || false, // Ensure boolean
        deadline: kr.deadline || undefined
      };
    });
    
    const total = krs.length;
    const done = krs.filter(kr => kr.completed === true).length;
    const progress = total ? Math.round((done / total) * 100) : 0;
    
    // Prepare payload - only send necessary fields
    const payload = {
      objective: target.objective,
      deadline: target.deadline,
      level: target.level,
      owners: target.owners || [target.owner].filter(Boolean),
      owner: target.owner,
      year: target.year,
      quarter: target.quarter,
      type: target.type,
      keyResults: krs,
      progress,
      score: target.score || 0,
      status: target.status
    };
    
    console.log('📤 [MyAnnualOKRs] Sending payload:', {
      okrId,
      keyResultsCount: payload.keyResults.length,
      completedCount: payload.keyResults.filter(kr => kr.completed).length,
      progress: payload.progress
    });
    
    // Optimistically update UI
    setOkrs(prev => prev.map(o => o._id === okrId ? { ...target, ...payload } : o));
    
    try {
      const res = await apiPut(`/okrs/${okrId}`, token, payload);
      if (res.ok) {
        // Update with server response to ensure we have the saved data
        try {
          const savedOKR = await res.json();
          console.log('✅ [MyAnnualOKRs] Saved OKR received:', {
            id: savedOKR._id,
            keyResults: savedOKR.keyResults?.map((kr, i) => ({
              idx: i,
              text: kr.text?.substring(0, 20),
              completed: kr.completed,
              type: typeof kr.completed
            }))
          });
          setOkrs(prev => prev.map(o => o._id === okrId ? savedOKR : o));
        } catch (parseError) {
          console.error('❌ [MyAnnualOKRs] Error parsing saved OKR response:', parseError);
          // Keep the optimistic update if parsing fails
        }
      } else {
        // Revert on failure
        setOkrs(prev => prev.map(o => o._id === okrId ? target : o));
        const errorText = await res.text().catch(() => 'Unknown error');
        console.error('❌ [MyAnnualOKRs] Failed to update OKR:', res.status, errorText);
        alert('Failed to save changes. Please try again.');
      }
    } catch (err) {
      // Revert on error
      setOkrs(prev => prev.map(o => o._id === okrId ? target : o));
      console.error('❌ [MyAnnualOKRs] Error updating OKR:', err);
      alert('Failed to save changes. Please try again.');
    }
  }

  // delete OKR
  const handleDeleteOKR = async id => {
    try {
      const res = await apiDelete(`/okrs/${id}`, token)
      if (!res.ok) throw new Error('Delete failed')
      setOkrs(prev => prev.filter(o => o._id !== id))
    } catch (err) {
      console.error('Error deleting OKR:', err)
    }
  }

  // expand / collapse controls
  const toggleExpand = id =>
    setExpandedMap(m => ({ ...m, [id]: !m[id] }))
  const expandAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, true])))
  const collapseAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, false])))

  // compute page-wide stats
  const { overallProgress, overallScore, overallStatus, totalOkrs, totalKRs } = (() => {
    const totalOkrs = okrs.length
    let totalKRs = 0, done = 0, sum = 0
    okrs.forEach(o => {
      totalKRs += o.keyResults.length
      done     += o.keyResults.filter(kr => kr.completed).length
      sum      += o.score || 0
    })
    const overallProgress = totalKRs ? Math.round((done / totalKRs) * 100) : 0
    const overallScore = totalOkrs ? sum / totalOkrs : 0
    let overallStatus = 'Not Started'
    if (overallProgress === 100) overallStatus = 'Completed'
    else if (overallProgress > 0) overallStatus = 'In Progress'
    return { overallProgress, overallScore, overallStatus, totalOkrs, totalKRs }
  })()

  if (loading) return <p className="center-message">Loading ANNUAL OKRs…</p>
  if (error)   return <p className="center-message error">Error: {error}</p>

  return (
    <div className="my-okrs-page">
      {/* Top Section */}
      <div className="my-okrs-top-section">
        <div className="my-okrs-header-row">
          <div className="my-okrs-left">
            <button className="plus-btn" onClick={handleCreateOKR}>
              <FaPlus />
            </button>
            <h2 style={{ margin: 0, color: '#004080' }}>Annual OKRs</h2>
          </div>
          <div className="my-okrs-right">
            <label>
              <input type="radio" checked={!isQuarterly} readOnly />
              Annual
            </label>
            <label>
              <input type="radio" checked={isQuarterly} onChange={switchToQuarter} />
              Quarter
            </label>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="my-okrs-progress-row">
        <div className="progress-details">
          <span>Overall Progress: <strong>{overallProgress}%</strong></span>
          <span>Score: <strong>{overallScore.toFixed(1)}</strong></span>
          <span>Status: <strong>{overallStatus}</strong></span>
          <span>OB: <strong>{totalOkrs}</strong></span>
          <span>KR: <strong>{totalKRs}</strong></span>
        </div>
        <div className="status-colors">
          <span className="status on-track">On Track</span>
          <span className="status behind">Behind</span>
          <span className="status at-risk">At Risk</span>
          <span className="status back-burner">Back Burner</span>
          <span className="status not-started">Not Started</span>
        </div>
      </div>

      {/* Expand / Collapse */}
      <div className="expand-collapse-container">
        <button onClick={expandAll}>Expand All</button>
        <button onClick={collapseAll}>Collapse All</button>
      </div>

      {/* OKRs Table */}
      {okrs.length === 0 ? (
        <div className="my-okrs-empty">
          <p>No ANNUAL OKRs created yet</p>
          <button onClick={handleCreateOKR}>Create OKR</button>
        </div>
      ) : (
        <table className="okrs-table">
          <thead>
            <tr>
              <th>Objective</th>
              <th>Owner</th>
              <th>Level</th>
              <th>Period</th>
              <th>Progress</th>
              <th>Score</th>
              <th>Status</th>
              <th>Key Results</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {okrs.map(okr => {
              const isOpen = expandedMap[okr._id]
              return (
                <React.Fragment key={okr._id}>
                  <tr className="okr-summary">
                    <td>{okr.objective}</td>
                    <td>{okr.owner}</td>
                    <td>{okr.level}</td>
                    <td>ANNUAL {okr.year}</td>
                    <td>{okr.progress}%</td>
                    <td>{okr.score}</td>
                    <td>
                      <span className={`status-pill ${okr.status.replace(/\s+/g,'-').toLowerCase()}`}>
                        {okr.status}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => toggleExpand(okr._id)}>
                        {isOpen ? 'Collapse' : 'Expand'}
                      </button>
                    </td>
                    <td>
                      <button onClick={() => handleEditOKR(okr._id)}>Edit</button>
                      <button onClick={() => handleDeleteOKR(okr._id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="okr-details-row">
                      <td colSpan="9">
                        <ul>
                          {okr.keyResults.map((kr, i) => (
                            <li key={i}>
                              <input
                                type="checkbox"
                                checked={kr.completed}
                                onChange={() => handleToggleKR(okr._id, i)}
                              />
                              {kr.text}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
